import { urlFor } from "@/utils/client";
import { IBlogPost, IBlogPosts } from "@/utils/types";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { SliderArrows } from "./sliderArrows";

const DEFAULT_POSTS_PER_PAGE = 3;
const NEWS_POSTS_PER_PAGE = 6;

function BlogPostCard({
  post,
  currentPath,
}: {
  post: IBlogPost;
  currentPath: string;
}) {
  return (
    <Link
      href={`/blog/${post.slug.current}?from=${encodeURIComponent(
        currentPath
      )}`}
      className="bg-white flex flex-col h-full text-left no-underline hover:no-underline group"
    >
      {post.image?.image && (
        <Image
          width={640}
          height={360}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          alt={post.image?.caption || post.title}
          src={urlFor(post.image.image).url()}
          className="w-full object-cover aspect-video"
        />
      )}
      <div className="p-6 flex flex-col gap-3 font-body flex-1">
        <span className="opacity-60">{post.tags?.join(" / ")}</span>
        <p className="font-display text-xl group-hover:underline">
          {post.title}
        </p>
        <div className="mt-auto flex flex-col gap-3">
          {post.date && (
            <span className="opacity-60">
              {format(new Date(post.date), "d MMMM yyyy", { locale: nl })}
            </span>
          )}
          <span className="flex items-center gap-2 italic text-sm">
            Lees meer
            <svg
              width="10"
              height="11"
              viewBox="0 0 10 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.24358 6.16363H0.214722V4.83655H7.24358L4.05364 1.6466L4.99997 0.714844L9.78521 5.50009L4.99997 10.2853L4.05364 9.35357L7.24358 6.16363Z"
                fill="currentColor"
              />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  );
}

export function BlogPosts(props: IBlogPosts) {
  const router = useRouter();
  const currentPath = router.asPath.split("?")[0].replace(/\/$/, "") || "/";
  const isArchive = currentPath === "/nieuws";
  const postsPerPage = isArchive ? NEWS_POSTS_PER_PAGE : DEFAULT_POSTS_PER_PAGE;
  const [visible, setVisible] = useState(postsPerPage);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const posts = (props.blogs ?? []).filter((post) => post.slug?.current);
  const allTags = Array.from(new Set(posts.flatMap((post) => post.tags ?? [])));

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: posts.length > 1,
    breakpoints: {
      "(max-width: 768px)": {
        slides: {
          perView: posts.length > 1 ? 1.15 : 1,
          spacing: 16,
          origin: "center",
        },
      },
    },
    slides: {
      perView: Math.min(posts.length || 1, 3.4),
      spacing: 30,
      origin: "center",
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel ?? 0);
    },
  });

  if (posts.length === 0) return null;

  const header = (props.title || props.description) && (
    <div className="flex flex-col component-gap text-center max-w-5xl px-4">
      {props.title && <p className="heading">{props.title}</p>}
      {props.description && <p className="opacity-80">{props.description}</p>}
    </div>
  );

  if (isArchive) {
    const filteredPosts = activeTag
      ? posts.filter((post) => post.tags?.includes(activeTag))
      : posts;
    const visiblePosts = filteredPosts.slice(0, visible);
    const hasMore = visible < filteredPosts.length;

    const selectTag = (tag: string | null) => {
      setActiveTag(tag);
      setVisible(postsPerPage);
    };

    return (
      <div className="flex flex-col items-center component-gap component-padding pt-8 md:pt-16">
        {header}
        {allTags.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2">
            {[null, ...allTags].map((tag) => (
              <button
                key={tag ?? "alles"}
                onClick={() => selectTag(tag)}
                className={`px-4 py-2 font-display transition-colors ${
                  activeTag === tag
                    ? "bg-darkGrey text-white"
                    : "bg-white text-black hover:bg-darkGrey/10"
                }`}
              >
                {tag
                  ? tag.charAt(0).toUpperCase() + tag.slice(1)
                  : "Alles"}
              </button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 component-gap w-full">
          {visiblePosts.map((post) => (
            <BlogPostCard
              key={post._id}
              post={post}
              currentPath={currentPath}
            />
          ))}
        </div>
        {hasMore && (
          <button
            onClick={() => setVisible((prev) => prev + postsPerPage)}
            className="bg-darkGrey text-white px-8 py-3 font-display hover:opacity-80 transition-opacity no-underline"
          >
            Meer laden
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center component-gap w-full overflow-hidden">
      {header}
      <div ref={sliderRef} className="keen-slider w-full">
        {posts.map((post, i) => (
          <div key={post._id} className="keen-slider__slide">
            <div
              className={`h-full transition-all duration-300 origin-center ${
                currentSlide !== i
                  ? "scale-[0.97] opacity-75"
                  : "scale-100 opacity-100"
              }`}
            >
              <BlogPostCard post={post} currentPath={currentPath} />
            </div>
          </div>
        ))}
      </div>
      {posts.length > 1 && (
        <SliderArrows
          onPrev={() => instanceRef.current?.prev()}
          onNext={() => instanceRef.current?.next()}
        />
      )}
    </div>
  );
}
